import { classes } from 'html-classes';
import { isEqual } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import { useDisclosure } from '~/hooks/useDisclosure';
import { useGlobal } from '~/hooks/useGlobal';
import { useSearchRecommendations } from '~/pages/SearchResults/hooks/useSearchRecommendations';
import { SearchRecommendations } from '~/pages/SearchResults/SearchRecommendations';
import { userStore } from '~/stores';
import { Product } from '~/stores/CategoriesStore';
import { searchStore } from '~/stores/SearchStore';
import { PaginationResult } from '~/stores/shared/Pagination';
import resetScroll from '~/utils/resetScroll';

import Icon from '../Icon/Icon';

const HeaderSearch = observer(() => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const popup = useDisclosure(false);
  const [recommendationsData, handleSelectBadge] = useSearchRecommendations();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchWrapperRef, _setSearchWrapperRef] = useState<
    HTMLElement | undefined
  >();
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const isOnSearchResultPage = pathname.startsWith('/search');

  const { isMobile } = useGlobal();

  const setSearchWrapperRef = (node: HTMLElement | null): void => {
    if (node && !isEqual(node, searchWrapperRef)) {
      _setSearchWrapperRef(node);
    }
  };

  const setQuery = (str?: string) => {
    if (str?.trim()) {
      searchParams.set('q', str);
    } else {
      searchParams.delete('q');
    }
    setSearchParams(searchParams, { replace: true });
  };

  const handleFocusInput = () => {
    if (isFocused) {
      return;
    }

    setIsFocused(true);
  };

  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>): void => {
    const value = (e.target as HTMLInputElement).value;

    if (e.code !== 'Enter' || !value) {
      return;
    }

    e.preventDefault();
    setQuery(value);
    searchStore.paginationRequest.debounceLoadPage(1, value);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    searchStore.setQuery(e.target.value);
    setQuery(e.target.value);
    searchStore.paginationRequest.debounceLoadPage(1, e.target.value);
  };

  const handleOutsideClick = useCallback(
    (e: Event): void => {
      const target = e.target as HTMLElement;

      if (searchWrapperRef?.contains(target)) {
        return;
      }

      if (isFocused) {
        setIsFocused(false);
      }
    },
    [searchWrapperRef, isFocused],
  );

  const handleReset = () => {
    searchStore.reset();
    setQuery();
    resetScroll({ isMobile });
  };

  useEffect(() => {
    const currentQueryString = searchParams.get('q');

    if (!searchStore.queryString && currentQueryString) {
      searchStore.setQuery(currentQueryString ?? '');
      searchStore.paginationRequest.debounceLoadPage(1, currentQueryString);
    } else if (searchStore.queryString) {
      searchParams.set('q', searchStore.queryString);
      setSearchParams(searchParams, { replace: true });
    }
  }, [isOnSearchResultPage]);

  useEffect(() => {
    const handler = (data: PaginationResult<Product> | undefined) => {
      if (data && !isOnSearchResultPage) {
        navigate('/search', { state: { source: pathname } });
      }
    };

    searchStore.paginationRequest.on('success', handler);
    return () => searchStore.paginationRequest.off('success', handler);
  }, [navigate, isOnSearchResultPage]);

  useEffect(() => {
    window.addEventListener('click', handleOutsideClick);

    return () => {
      window.removeEventListener('click', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  return (
    <>
      <div
        className={classes([
          'header-search',
          { 'header-search--focused': isFocused },
        ])}
        ref={setSearchWrapperRef}
        onFocus={handleFocusInput}
        data-company={company.name}
      >
        {searchStore.paginationRequest.isLoading ? (
          <div className="spinner header-search__icon" />
        ) : (
          <Icon className="header-search__icon" type="search" size={24} />
        )}

        <input
          data-input="search"
          type="text"
          value={searchStore.queryString}
          className="header-search__input"
          placeholder={t('phrases:hiThere', {
            name: userStore.personalData.firstName || 'there',
          })}
          onKeyUp={handleKeyUp}
          onChange={handleInputChange}
          onFocus={popup.open}
          onBlur={() => setTimeout(popup.close, 200)}
        />
        <button
          className="button _no-padding"
          style={{ visibility: searchStore.queryString ? 'visible' : 'hidden' }}
          onClick={handleReset}
        >
          <Icon type="close" />
        </button>
        <div
          className={classes([
            'search-popup',
            popup.visible &&
              recommendationsData.length &&
              'search-popup-visible',
          ])}
        >
          <SearchRecommendations
            data={recommendationsData}
            onSelect={handleSelectBadge}
          />
        </div>
      </div>
    </>
  );
});

export default HeaderSearch;
